// wp/ACTIONS: the board settings sheet's "Fields" tab — the board's custom fields as a list, for
// whoever may edit the board: add one (a name and a type; a choice field takes its choices),
// and per row, as hover icons, Edit (name, and a choice field's choices) and Delete (asks first:
// every task's value for it goes). `fields.create / update / delete` do the work; the server's
// refusal (a name taken, bad options) shows in the dialog or under the list as it reads.
// Formula, relation and rollup fields need their own options and stay where they are made.
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  Badge,
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Field,
  FieldDescription,
  FieldError,
  FieldLabel,
  Input,
  NativeSelect,
  NativeSelectOption,
} from "@companyos/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { PencilIcon, PlusIcon, Trash2Icon, XIcon } from "lucide-react";
import { type FormEvent, useId, useState } from "react";
import type { Api } from "../../../api/client";
import { problemText } from "../../settings/problem-text";
import type { FieldDef } from "../view-engine";
import { fieldsQuery, invalidateWork, workKeys } from "../work-queries";
import { type BoardSettingsTabProps, registerBoardSettingsTab } from "./registry";

type FieldType = FieldDef["type"];
type Choice = NonNullable<FieldDef["options"]["choices"]>[number];

/** The types made here (the others need options this tab does not edit). */
export const MADE_HERE: { value: FieldType; label: string }[] = [
  { value: "text", label: "Text" },
  { value: "number", label: "Number" },
  { value: "currency", label: "Currency" },
  { value: "date", label: "Date" },
  { value: "checkbox", label: "Checkbox" },
  { value: "url", label: "Link" },
  { value: "seat", label: "Person" },
  { value: "select", label: "Single choice" },
  { value: "multi_select", label: "Multiple choice" },
];

const typeLabel = (t: FieldType) =>
  MADE_HERE.find((x) => x.value === t)?.label ?? t.charAt(0).toUpperCase() + t.slice(1);
const hasChoices = (t: FieldType) => t === "select" || t === "multi_select";

/** One row of the choices editor: `key` set for a choice that already exists (its key never
 *  changes, so a rename keeps every task's value), null for one added here. */
export type ChoiceRow = { key: string | null; name: string };

/** The choices to save from the editor's rows: an existing choice keeps its key and colour under
 *  its (maybe new) name. A choice added to a field that already exists gets a key with a random
 *  `suffix`: a choice removed in an earlier save may still be set on tasks under its old key, and
 *  a new choice must never pick that key up (old values would silently take its meaning). A new
 *  field has no values yet, so its keys are plain. Blank and repeated names drop out. */
export function choicesFrom(
  rows: readonly ChoiceRow[],
  existing: readonly Choice[] = [],
  suffix?: () => string,
): Choice[] {
  const taken = new Set<string>(existing.map((c) => c.key));
  const out: Choice[] = [];
  for (const row of rows) {
    const name = row.name.trim();
    if (!name || out.some((c) => c.name === name)) continue;
    const kept = row.key ? existing.find((c) => c.key === row.key) : undefined;
    if (kept) {
      out.push({ ...kept, name });
      continue;
    }
    const base =
      name
        .toLowerCase()
        .normalize("NFKD")
        .replace(/[^a-z0-9]+/g, "_")
        .replace(/^_+|_+$/g, "")
        .slice(0, 40) || "choice";
    let key = suffix ? `${base}_${suffix()}` : base;
    for (let n = 2; taken.has(key); n++) key = `${base}_${n}`;
    taken.add(key);
    out.push({ key, name });
  }
  return out;
}

export function FieldsTab({ board, api, co }: BoardSettingsTabProps) {
  if (!api || !co) return null;
  return <FieldsList api={api} co={co} boardId={board.id} archived={Boolean(board.archived_at)} />;
}

function FieldsList({
  api,
  co,
  boardId,
  archived,
}: {
  api: Api;
  co: string;
  boardId: string;
  archived: boolean;
}) {
  const qc = useQueryClient();
  const fields = useQuery(fieldsQuery(api, co, boardId));
  const [editing, setEditing] = useState<FieldDef | "new" | null>(null);
  const [deleting, setDeleting] = useState<FieldDef | null>(null);
  const [error, setError] = useState<string | null>(null);
  const refresh = async () => {
    await qc.invalidateQueries({ queryKey: workKeys.fields(co, boardId) });
    await invalidateWork(qc, co);
  };
  const remove = useMutation({
    mutationFn: (f: FieldDef) =>
      api.client.fields.delete({ params: { co, id: boardId, sub_id: f.id } }),
    onMutate: () => setError(null),
    onError: (e) => setError(problemText(e, "The field could not be deleted.")),
    onSettled: refresh,
  });
  const rows = fields.data?.data ?? [];
  return (
    <div data-tab="fields" className="flex flex-col gap-2 text-sm">
      {rows.length ? (
        <ul aria-label="Fields" className="m-0 flex list-none flex-col p-0">
          {rows.map((f) => (
            <li
              key={f.id}
              data-field={f.id}
              className="group/field flex min-h-9 items-center gap-2 rounded-md px-1 hover:bg-muted"
            >
              <span className="min-w-0 flex-1 truncate">{f.name}</span>
              <Badge variant="neutral">{typeLabel(f.type)}</Badge>
              {archived ? null : (
                <span className="flex items-center opacity-0 group-hover/field:opacity-100 group-focus-within/field:opacity-100 pointer-coarse:opacity-100">
                  <Button
                    variant="ghost"
                    size="icon-xs"
                    aria-label={`Edit ${f.name}`}
                    title="Edit"
                    onClick={() => setEditing(f)}
                  >
                    <PencilIcon aria-hidden="true" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon-xs"
                    aria-label={`Delete ${f.name}`}
                    title="Delete"
                    className="hover:text-destructive"
                    onClick={() => setDeleting(f)}
                  >
                    <Trash2Icon aria-hidden="true" />
                  </Button>
                </span>
              )}
            </li>
          ))}
        </ul>
      ) : fields.isPending ? null : (
        <p className="text-muted-foreground">No custom fields yet.</p>
      )}
      {archived ? null : (
        <Button
          variant="outline"
          size="sm"
          className="self-start"
          onClick={() => setEditing("new")}
        >
          <PlusIcon aria-hidden="true" />
          New field
        </Button>
      )}
      {error ? (
        <p role="alert" className="text-xs text-danger-text">
          {error}
        </p>
      ) : null}
      {editing ? (
        <FieldDialog
          api={api}
          co={co}
          boardId={boardId}
          field={editing === "new" ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={refresh}
        />
      ) : null}
      <AlertDialog open={deleting !== null} onOpenChange={(o) => !o && setDeleting(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete the field {deleting?.name}?</AlertDialogTitle>
            <AlertDialogDescription>
              Every task's value for it goes, and views grouped by it fall back to status. This
              can't be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Keep it</AlertDialogCancel>
            <AlertDialogAction
              variant="destructive"
              onClick={() => {
                if (deleting) remove.mutate(deleting);
                setDeleting(null);
              }}
            >
              Delete field
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function FieldDialog({
  api,
  co,
  boardId,
  field,
  onClose,
  onSaved,
}: {
  api: Api;
  co: string;
  boardId: string;
  /** null: a new field. */
  field: FieldDef | null;
  onClose: () => void;
  onSaved: () => Promise<unknown>;
}) {
  const ids = { name: useId(), type: useId(), choices: useId() };
  const [name, setName] = useState(field?.name ?? "");
  const [type, setType] = useState<FieldType>(field?.type ?? "text");
  const existing = field?.options.choices ?? [];
  const [rows, setRows] = useState<ChoiceRow[]>(
    existing.length
      ? existing.map((c) => ({ key: c.key, name: c.name }))
      : [{ key: null, name: "" }],
  );
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);
  const next = hasChoices(type) ? choicesFrom(rows, existing) : [];
  // Existing choices this edit drops: tasks set to one lose that value, so saving asks first.
  const dropped = hasChoices(type)
    ? existing.filter((c) => !next.some((x) => x.key === c.key))
    : [];
  const [confirmDrop, setConfirmDrop] = useState(false);
  const setRow = (i: number, row: ChoiceRow | null) =>
    setRows((rs) => (row ? rs.map((r, j) => (j === i ? row : r)) : rs.filter((_, j) => j !== i)));
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    const n = name.trim();
    if (!n) return;
    if (dropped.length && !confirmDrop) {
      setConfirmDrop(true);
      return;
    }
    setPending(true);
    setError(null);
    try {
      const options = hasChoices(type)
        ? {
            options: {
              ...(field?.options ?? {}),
              choices: choicesFrom(
                rows,
                existing,
                field ? () => crypto.randomUUID().slice(0, 8) : undefined,
              ),
            },
          }
        : {};
      if (field)
        await api.client.fields.update({
          params: { co, id: boardId, sub_id: field.id },
          body: { ...(n !== field.name ? { name: n } : {}), ...options },
        });
      else
        await api.client.fields.create({
          params: { co, id: boardId },
          body: { name: n, type, ...options },
        });
      await onSaved();
      onClose();
    } catch (err) {
      setError(problemText(err, field ? "The field could not be saved." : "No field was made."));
    } finally {
      setPending(false);
    }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-sm">
        <form className="grid gap-4" onSubmit={submit}>
          <DialogHeader>
            <DialogTitle>{field ? `Edit ${field.name}` : "New field"}</DialogTitle>
            <DialogDescription>Everyone on this board sees the field.</DialogDescription>
          </DialogHeader>
          <Field>
            <FieldLabel htmlFor={ids.name}>Name</FieldLabel>
            <Input
              id={ids.name}
              required
              maxLength={200}
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </Field>
          <Field>
            <FieldLabel htmlFor={ids.type}>Type</FieldLabel>
            <NativeSelect
              id={ids.type}
              value={type}
              disabled={field !== null}
              onChange={(e) => setType(e.target.value as FieldType)}
            >
              {(field && !MADE_HERE.some((t) => t.value === field.type)
                ? [{ value: field.type, label: typeLabel(field.type) }, ...MADE_HERE]
                : MADE_HERE
              ).map((t) => (
                <NativeSelectOption key={t.value} value={t.value}>
                  {t.label}
                </NativeSelectOption>
              ))}
            </NativeSelect>
            {field ? <FieldDescription>A field's type never changes.</FieldDescription> : null}
          </Field>
          {hasChoices(type) ? (
            <Field>
              <FieldLabel id={ids.choices}>Choices</FieldLabel>
              <ul aria-labelledby={ids.choices} className="m-0 flex list-none flex-col gap-1 p-0">
                {rows.map((r, i) => (
                  <li
                    // biome-ignore lint/suspicious/noArrayIndexKey: rows have no id until saved
                    key={r.key ?? `new-${i}`}
                    className="flex items-center gap-1"
                  >
                    <Input
                      aria-label={`Choice ${i + 1}`}
                      maxLength={100}
                      placeholder={i === 0 ? "Low" : ""}
                      value={r.name}
                      onChange={(e) => {
                        setConfirmDrop(false);
                        setRow(i, { ...r, name: e.target.value });
                      }}
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon-xs"
                      aria-label={`Remove choice ${r.name || i + 1}`}
                      title="Remove"
                      onClick={() => {
                        setConfirmDrop(false);
                        setRow(i, null);
                      }}
                    >
                      <XIcon aria-hidden="true" />
                    </Button>
                  </li>
                ))}
              </ul>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="self-start"
                onClick={() => setRows((rs) => [...rs, { key: null, name: "" }])}
              >
                <PlusIcon aria-hidden="true" />
                Add choice
              </Button>
              {confirmDrop && dropped.length ? (
                <FieldDescription role="alert" className="text-danger-text">
                  Tasks set to {dropped.map((c) => c.name).join(", ")} lose that value. Save again
                  to remove {dropped.length === 1 ? "it" : "them"}.
                </FieldDescription>
              ) : null}
            </Field>
          ) : null}
          {error ? <FieldError>{error}</FieldError> : null}
          <DialogFooter>
            <Button
              type="submit"
              variant={confirmDrop && dropped.length ? "destructive" : "default"}
              disabled={pending || !name.trim()}
            >
              {confirmDrop && dropped.length ? "Remove and save" : field ? "Save" : "Add field"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

const off = registerBoardSettingsTab({
  id: "fields",
  title: "Fields",
  order: 10,
  Component: FieldsTab,
  when: (p) => Boolean(p.api && p.co) && p.can.edit,
});
// A hot reload re-runs this module: the old registration goes first (no duplicate id).
import.meta.hot?.dispose(off);
